import { AlertTriangle, ExternalLink, KeyRound, Link2Off, Smartphone } from 'lucide-react';
import type { Empleado, Operativa } from '@/lib/personal';

/**
 * Lo que esta persona tiene en la app de lavandería: llave Nuki y acceso a la
 * app de housekeeping. En SOLO LECTURA.
 *
 * Está aquí para que la ficha conteste «¿tiene llave?» sin abrir otra
 * aplicación. Cambiarlo se sigue haciendo allí: el código Nuki tiene su propio
 * historial y su cron de revocación, y el acceso a housekeeping arrastra un
 * hash de contraseña. Duplicar eso aquí sería pedir que las dos versiones
 * acaben en desacuerdo.
 *
 * El alta y la baja SÍ se pueden cambiar en los dos sitios, así que este bloque
 * es también donde se ve cuando no coinciden. No se arregla solo a propósito:
 * el CRM guarda fecha y motivo, y readoptar en silencio lo que diga la
 * lavandería sería tirar justo eso.
 *
 * El código Nuki NUNCA se muestra, solo si lo tiene y desde cuándo.
 */
interface Props {
  empleado: Empleado;
  operativa: Operativa | null;
  onEnlazar: () => void;
}

const ROL_LABEL: Record<string, string> = {
  employee: 'Empleada',
  gobernanta: 'Gobernanta',
  admin: 'Administración',
};

export default function OperativaCard({ empleado, operativa, onEnlazar }: Props) {
  if (!empleado.lavanderiaId) {
    return (
      <div className="card border border-amber-200 bg-amber-50/60">
        <div className="flex items-start gap-3">
          <Link2Off size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-amber-800">Sin enlazar con la app de lavandería</h3>
            <p className="text-xs text-amber-700/80 mt-1">
              Mientras no se enlace, esta persona no aparece en los cuadrantes de limpieza ni puede fichar.
            </p>
            <button onClick={onEnlazar} className="btn-secondary text-xs mt-3">
              Enlazar ahora
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (operativa && !operativa.existe) {
    return (
      <div className="card border border-red-200 bg-red-50/60">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-red-800">Ya no existe en la app de lavandería</h3>
            <p className="text-xs text-red-700/80 mt-1">
              La ficha sigue enlazada con <code className="font-mono">{empleado.lavanderiaId}</code>, pero ese empleado
              se ha borrado allí. Los cuadrantes y los fichajes antiguos que lo mencionen se quedan sin dueño.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Operativa</h3>
        <span className="text-[11px] text-sereno-dark/40">Se gestiona en la app de lavandería</span>
      </div>

      {operativa?.discrepaSituacion && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5">
          <AlertTriangle size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            En la app de lavandería está como <strong>{operativa.activoEnLavanderia ? 'activa' : 'inactiva'}</strong>,
            que no es lo que dice esta ficha.{' '}
            {empleado.lavanderiaDesincronizada
              ? 'El último cambio hecho aquí no llegó a escribirse allí; se reintenta solo cada día.'
              : 'Alguien lo ha cambiado en la app de lavandería. Si la buena es esta ficha, vuelve a dar de alta o de baja desde aquí para que se propague.'}
          </p>
        </div>
      )}

      <dl className="space-y-2.5 text-sm">
        <div className="flex items-center gap-2">
          <KeyRound size={15} className="text-sereno-dark/40 flex-shrink-0" />
          <dt className="text-sereno-dark/50 flex-1">Llave Nuki</dt>
          <dd className="font-medium">
            {operativa?.tieneNukiCode ? (
              <span className="text-green-700">
                Sí
                {operativa.nukiDesde && (
                  <span className="text-sereno-dark/40 font-normal">
                    {' '}
                    · desde {new Date(operativa.nukiDesde).toLocaleDateString('es-ES')}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-sereno-dark/40">No tiene</span>
            )}
          </dd>
        </div>

        <div className="flex items-center gap-2">
          <Smartphone size={15} className="text-sereno-dark/40 flex-shrink-0" />
          <dt className="text-sereno-dark/50 flex-1">App de housekeeping</dt>
          <dd className="font-medium text-right">
            {operativa?.housekeepingActivo ? (
              <>
                <span className="font-mono text-xs">{operativa.housekeepingUsuario}</span>
                {operativa.housekeepingRol && (
                  <span className="text-sereno-dark/40 font-normal text-xs">
                    {' '}
                    · {ROL_LABEL[operativa.housekeepingRol] || operativa.housekeepingRol}
                  </span>
                )}
              </>
            ) : (
              <span className="text-sereno-dark/40">Sin acceso</span>
            )}
          </dd>
        </div>
      </dl>

      <a
        href="https://lavanderia.tusereno.es"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-sereno-dark/50 hover:text-sereno-dark transition-colors"
      >
        Abrir la app de lavandería <ExternalLink size={12} />
      </a>
    </div>
  );
}
